import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { WHATS_NEW, WHATS_NEW_VERSION } from '../data/whatsNew';

const SEEN_KEY = 'papo_whats_new_seen';
export const OPEN_WHATS_NEW = 'papo:whats-new';
const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password', '/reset-password'];

export const WhatsNewModal = () => {
  const { user } = useAuth();
  const { language } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const c = WHATS_NEW[language] || WHATS_NEW.pt;

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_WHATS_NEW, show);
    return () => window.removeEventListener(OPEN_WHATS_NEW, show);
  }, []);

  useEffect(() => {
    if (!user || user.onboarding_active || PUBLIC_PATHS.includes(location.pathname)) return;
    if (localStorage.getItem(SEEN_KEY) === WHATS_NEW_VERSION) return;
    const id = setInterval(() => {
      if (document.querySelector('[role="dialog"], [data-testid="onboarding-tour"]')) return;
      clearInterval(id);
      setOpen(true);
    }, 2000);
    return () => clearInterval(id);
  }, [user, location.pathname]);

  const close = () => { localStorage.setItem(SEEN_KEY, WHATS_NEW_VERSION); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-3xl max-w-md w-[calc(100vw-32px)] p-6 max-h-[85vh] overflow-y-auto" data-testid="whats-new-modal">
        <DialogHeader className="text-left">
          <div className="w-12 h-12 rounded-2xl gradient-warm flex items-center justify-center mb-2"><Sparkles className="w-6 h-6 text-white" /></div>
          <DialogTitle className="font-['Nunito'] text-2xl" data-testid="whats-new-title">{c.title}</DialogTitle>
          <DialogDescription>{c.subtitle}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-3 my-2">
          {c.items.map(([title, desc], i) => {
            const Icon = WHATS_NEW.icons[i];
            return (
              <li key={title} className="flex gap-3 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'both' }} data-testid={`whats-new-item-${i}`}>
                <div className="w-9 h-9 rounded-xl bg-[var(--elevated-bg)] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[var(--papo-gold)]" /></div>
                <div><p className="font-bold text-sm text-[var(--text-primary)]">{title}</p><p className="text-xs text-[var(--text-secondary)]">{desc}</p></div>
              </li>
            );
          })}
        </ul>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { close(); navigate('/help'); }} className="flex-1 rounded-full" data-testid="whats-new-guide">{c.guide}</Button>
          <Button onClick={close} className="flex-1 rounded-full gradient-warm text-white font-bold" data-testid="whats-new-ok">{c.ok}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
